import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Search, ShieldCheck, Clock, MapPin, Send } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { apiClient, formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";
import {
  PROPERTY_TYPES, RENTAL_STATUS, RENTAL_MODES, SERVICES, TIMINGS,
  Choice, StepTitle, Counter, inputCls,
} from "../components/quote/QuoteParts";

const TOTAL = 5;

export default function QuotePage() {
  const [params] = useSearchParams();
  const { state } = useLocation();
  const sim = state?.simulation;
  const [cities, setCities] = useState([]);
  const [citiesError, setCitiesError] = useState(false);
  const [search, setSearch] = useState("");
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    city_slug: params.get("ville") || sim?.city_slug || "",
    city_other: "",
    property_type: sim?.property_type || "",
    bedrooms: sim?.bedrooms ?? 2,
    capacity: sim?.capacity ?? 4,
    rental_status: "",
    rental_mode: "",
    services: ["Gestion complète"],
    start_timing: "",
    first_name: "", last_name: "", email: "", phone: "", message: "",
    consent: false,
    simulator_low: sim?.low ?? null,
    simulator_high: sim?.high ?? null,
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    apiClient.get("/cities").then((r) => setCities(r.data)).catch(() => setCitiesError(true));
  }, []);

  const filtered = useMemo(
    () => {
      const nz = (x) => x.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
      const list = cities.filter((c) => nz(c.name).includes(nz(search)));
      const sel = list.find((c) => c.slug === form.city_slug);
      return sel ? [sel, ...list.filter((c) => c !== sel)] : list;
    },
    [cities, search, form.city_slug]
  );
  const fromCity = cities.find((c) => c.slug === params.get("ville"))?.name;
  const cityName = cities.find((c) => c.slug === form.city_slug)?.name || form.city_other;

  const canNext = [
    !!form.city_slug || form.city_other.trim().length > 1 || search.trim().length > 1,
    !!form.property_type,
    !!form.rental_status && !!form.rental_mode,
    form.services.length > 0 && !!form.start_timing,
    form.first_name && form.last_name && /\S+@\S+\.\S+/.test(form.email) && form.phone.length >= 6 && form.consent,
  ][step];

  const toggleService = (s) =>
    set({ services: form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s] });

  const submit = async () => {
    setSending(true);
    try {
      await apiClient.post("/devis", { ...form, city_slug: form.city_slug || null, city_other: form.city_slug ? null : form.city_other });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Comparateur de conciergerie Airbnb",
    provider: { "@type": "Organization", name: "Bretagne Conciergerie Comparateur" },
    areaServed: cities.map((c) => ({ "@type": "City", name: c.name })),
    description: "Demande de devis gratuite : Bretagne Conciergerie Comparateur vous met en relation avec une conciergerie Airbnb adaptée à votre bien, partout en Bretagne.",
  };

  return (
    <PublicLayout>
      <SEO
        title="Devis conciergerie Airbnb gratuit partout en Bretagne"
        description="Comparez les conciergeries Airbnb sans démarcher : une seule demande de devis gratuite, nous vous mettons en relation avec la conciergerie adaptée à votre bien (Saint-Malo, Dinard, Rennes, Saint-Brieuc, Granville…)."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Toaster position="top-right" richColors />

      <section className="pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="container-tm max-w-5xl">
          <span className="pill">{fromCity ? `0 € pour vous · ${fromCity}` : "0 € pour le propriétaire"}</span>
          <h1 className="h-display mt-5" data-testid="quote-title">
            {fromCity ? (
              <>Votre devis conciergerie Airbnb<br /><span className="text-accent">à {fromCity}</span></>
            ) : (
              <>Comparez les conciergeries,<br /><span className="text-accent">sans les démarcher.</span></>
            )}
          </h1>
          <p className="mt-5 text-muted-foreground text-lg max-w-2xl">
            Décrivez votre bien en 2 minutes. Un membre de notre équipe vous rappelle pour comprendre vos besoins,
            puis vous oriente vers la conciergerie la plus adaptée à votre secteur. <strong className="text-foreground">0 € pour le propriétaire</strong>, sans engagement.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-tm max-w-5xl grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            {sent ? (
              <Confirmation name={form.first_name} />
            ) : (
              <>
                <div className="flex items-center gap-2 mb-8" data-testid="quote-stepper">
                  {Array.from({ length: TOTAL }).map((_, i) => (
                    <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? "bg-accent" : "bg-border"}`} />
                  ))}
                  <div className="text-sm font-bold text-muted-foreground ml-2 shrink-0">{step + 1}/{TOTAL}</div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.25 }}
                    className="surface-card p-6 md:p-10"
                    data-testid={`quote-step-${step}`}
                  >
                    {step === 0 && (
                      <div>
                        <StepTitle index={1} total={TOTAL} title="Où se trouve votre bien ?" hint="Partout en Bretagne, de la ville au petit village." />
                        <div className="relative mt-6">
                          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une commune…" className={`${inputCls} pl-11`} data-testid="quote-city-search" />
                        </div>
                        {search.trim().length > 1 && filtered.length === 0 && (
                          <p className="mt-4 text-sm text-muted-foreground" data-testid="quote-city-no-match">
                            {citiesError ? "La liste des communes ne s'est pas chargée." : "Commune introuvable dans la liste."} Pas de souci : cliquez sur « Suivant », nous noterons « {search.trim()} ».
                          </p>
                        )}
                        <div className="mt-4 grid sm:grid-cols-2 gap-2.5 max-h-[340px] overflow-y-auto pr-1">
                          {filtered.slice(0, 60).map((c) => (
                            <Choice key={c.slug} active={form.city_slug === c.slug} onClick={() => set({ city_slug: c.slug, city_other: "" })}
                              title={c.name} desc={c.department} testid={`quote-city-${c.slug}`} />
                          ))}
                        </div>
                        <div className="mt-5">
                          <label className="text-sm font-semibold">Votre commune n&apos;est pas dans la liste ?</label>
                          <input value={form.city_other} onChange={(e) => set({ city_other: e.target.value, city_slug: "" })} placeholder="Ex : Saint-Pair-sur-Mer" className={`${inputCls} mt-2`} data-testid="quote-city-other" />
                        </div>
                      </div>
                    )}

                    {step === 1 && (
                      <div>
                        <StepTitle index={2} total={TOTAL} title="Quel type de bien ?" />
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-2.5">
                          {PROPERTY_TYPES.map((t) => (
                            <Choice key={t.v} center active={form.property_type === t.v} onClick={() => set({ property_type: t.v })} title={t.l} testid={`quote-type-${t.v}`} />
                          ))}
                        </div>
                        <div className="mt-6 grid sm:grid-cols-2 gap-3">
                          <Counter label="Chambres" value={form.bedrooms} min={0} max={20} onChange={(v) => set({ bedrooms: v })} testid="quote-bedrooms" />
                          <Counter label="Voyageurs" value={form.capacity} min={1} max={30} onChange={(v) => set({ capacity: v })} testid="quote-capacity" />
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div>
                        <StepTitle index={3} total={TOTAL} title="Où en êtes-vous ?" />
                        <div className="mt-6 grid gap-2.5">
                          {RENTAL_STATUS.map((r) => (
                            <Choice key={r.v} active={form.rental_status === r.v} onClick={() => set({ rental_status: r.v })} title={r.l} desc={r.d} testid={`quote-status-${r.v}`} />
                          ))}
                        </div>
                        <div className="mt-6 text-sm font-semibold">Vous souhaitez louer…</div>
                        <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
                          {RENTAL_MODES.map((m) => (
                            <Choice key={m.v} center active={form.rental_mode === m.v} onClick={() => set({ rental_mode: m.v })} title={m.l} testid={`quote-mode-${m.v}`} />
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div>
                        <StepTitle index={4} total={TOTAL} title="De quels services avez-vous besoin ?" hint="Plusieurs choix possibles." />
                        <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
                          {SERVICES.map((s) => (
                            <Choice key={s} active={form.services.includes(s)} onClick={() => toggleService(s)} title={s} testid={`quote-service-${s}`} />
                          ))}
                        </div>
                        <div className="mt-6 text-sm font-semibold">Quand souhaitez-vous démarrer ?</div>
                        <div className="mt-3 grid sm:grid-cols-3 gap-2.5">
                          {TIMINGS.map((t) => (
                            <Choice key={t.v} center active={form.start_timing === t.v} onClick={() => set({ start_timing: t.v })} title={t.l} testid={`quote-timing-${t.v}`} />
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 4 && (
                      <div>
                        <StepTitle index={5} total={TOTAL} title="Où vous envoyer votre devis ?" hint="Vos coordonnées sont transmises uniquement à la conciergerie sélectionnée." />
                        <div className="mt-6 grid sm:grid-cols-2 gap-3">
                          <input placeholder="Prénom *" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} className={inputCls} data-testid="quote-first-name" />
                          <input placeholder="Nom *" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} className={inputCls} data-testid="quote-last-name" />
                          <input placeholder="Email *" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={inputCls} data-testid="quote-email" />
                          <input placeholder="Téléphone *" value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={inputCls} data-testid="quote-phone" />
                        </div>
                        <textarea placeholder="Précisions sur votre bien (facultatif)" rows={3} value={form.message} onChange={(e) => set({ message: e.target.value })}
                          className="mt-3 w-full px-5 py-4 border border-input rounded-2xl bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" data-testid="quote-message" />
                        <label className="mt-4 flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                          <input type="checkbox" checked={form.consent} onChange={(e) => set({ consent: e.target.checked })} className="mt-1 accent-[hsl(var(--accent))] w-4 h-4" data-testid="quote-consent" />
                          J&apos;accepte que Bretagne Conciergerie Comparateur me rappelle et transmette ma demande à une conciergerie partenaire adaptée, selon la <a href="/politique-de-confidentialite" target="_blank" rel="noopener noreferrer" className="underline">politique de confidentialité</a>.
                        </label>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="flex justify-between mt-6" data-testid="quote-nav">
                  <button onClick={() => setStep((s) => s - 1)} disabled={step === 0} className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid="quote-prev">
                    <ArrowLeft size={14} /> Précédent
                  </button>
                  {step < TOTAL - 1 ? (
                    <button onClick={() => { if (step === 0 && !form.city_slug && !form.city_other.trim()) set({ city_other: search.trim() }); setStep((s) => s + 1); }} disabled={!canNext} className="btn-primary disabled:opacity-50" data-testid="quote-next">
                      Suivant <ArrowRight size={14} />
                    </button>
                  ) : (
                    <button onClick={submit} disabled={!canNext || sending} className="btn-accent disabled:opacity-50" data-testid="quote-submit">
                      <Send size={14} /> {sending ? "Envoi…" : "Recevoir mon devis gratuit"}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          <aside className="lg:col-span-4 space-y-4" data-testid="quote-sidebar">
            {cityName && !sent && (
              <div className="surface-card p-5 flex items-center gap-3" data-testid="quote-summary-city">
                <MapPin size={18} className="text-accent" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Votre secteur</div>
                  <div className="font-bold">{cityName}</div>
                </div>
              </div>
            )}
            {form.simulator_low && !sent && (
              <div className="surface-card p-5" data-testid="quote-summary-sim">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Votre estimation</div>
                <div className="font-bold text-xl text-accent mt-1">{form.simulator_low.toLocaleString("fr-FR")} – {form.simulator_high.toLocaleString("fr-FR")} €/an</div>
              </div>
            )}
            <div className="rounded-3xl bg-primary text-primary-foreground p-6 space-y-4">
              {[
                { icon: Check, t: "Une seule demande", d: "Fini les appels à 5 conciergeries différentes." },
                { icon: MapPin, t: "Une conciergerie locale", d: "Choisie selon votre commune et vos besoins." },
                { icon: Clock, t: "On vous rappelle", d: "Un membre de l'équipe vous appelle pour comprendre vos besoins. Joignables 7j/7, 10h-20h." },
                { icon: ShieldCheck, t: "0 € pour le propriétaire", d: "Notre service est gratuit et sans engagement : vous restez libre d'accepter ou non le devis." },
              ].map((b) => (
                <div key={b.t} className="flex gap-3">
                  <span className="w-8 h-8 rounded-full bg-[hsl(var(--ocean))] text-white flex items-center justify-center shrink-0"><b.icon size={14} strokeWidth={2.5} /></span>
                  <div>
                    <div className="font-semibold text-sm">{b.t}</div>
                    <div className="text-xs text-white/70 mt-0.5">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}

function Confirmation({ name }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="surface-card p-10 md:p-14 text-center" data-testid="quote-success">
      <div className="inline-flex w-16 h-16 rounded-full bg-accent/10 text-accent items-center justify-center">
        <Check size={32} strokeWidth={2.5} />
      </div>
      <h2 className="h-card text-2xl md:text-3xl mt-5">Merci {name}, votre demande est bien reçue</h2>
      <p className="mt-3 text-muted-foreground max-w-md mx-auto">
        Un membre de notre équipe vous rappelle rapidement par téléphone pour bien comprendre vos besoins, puis vous oriente vers la conciergerie adaptée à votre bien.
      </p>
      <Link to="/blog" className="btn-ghost mt-8" data-testid="quote-success-blog">
        Lire nos conseils en attendant <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}
